/*
 * REFERENCE DATA — profile wizard step 3 (canon §5.3 / §11.8).
 *
 * `REF_COUNTRIES` now covers EVERY ISO-3166-1 country (§11.8 seam — a live test
 * hit a missing country, Thailand, when the list was a curated 24). It is built
 * once at module load by MERGING two sources, curated-first:
 *
 *  1. CURATED_COUNTRIES — the 24 launch-priority markets transcribed verbatim
 *     from the DC source (App Shell.dc.html `countries()`). These carry PRECISE
 *     national-number digit rules (min/max) and hand-picked city shortlists,
 *     which are better data than the generic dataset — so they take precedence.
 *  2. `world-countries` (maintained ISO-3166 dataset) — every OTHER country,
 *     mapped into `RefCountry`. Deduped against the curated set by ISO alpha-2.
 *     For these, precise national-number length is unknown, so permissive E.164
 *     bounds (4–15) are used, and the city shortlist is the country's capital.
 *
 * CITY SEARCH (CMT-100427): the per-country `RefCountry.c` above is only a tiny
 * seed (curated markets: a hand-picked handful; appended countries: JUST the
 * capital). That made the City field a one-city-per-country allow-list — a live
 * Thailand user typing "Ayutthaya" was silently blocked ("Choose a city in
 * Thailand") with no way to discover what was valid. We now BACK the city search
 * with a real bundled dataset (`countries-cities`, ~228 KB, 249 countries /
 * ~18 k major cities, offline + CSP-safe — no network fetch): `citiesFor()`
 * merges the curated seed (kept FIRST so launch-market ordering is unchanged)
 * with the dataset's major cities for that country. The typeahead searches the
 * merged list; `cityKnown()` reports membership so the wizard can offer a
 * deliberate free-text "use my entry" pick when a place isn't in the dataset —
 * so an unrecognised city is never a silent dead end.
 *
 * This module remains the ONLY place that knows the list; everything else goes
 * through its exported lookups. UAE stays first (curated order preserved) so any
 * default-country assumption downstream is unaffected.
 *
 * // TODO(§11.8 seam): production still wants a geo/city service (typo-tolerant
 * // search, districts, population ranking) and libphonenumber-driven per-country
 * // dial/digit validation. The dataset here (major cities only, English/
 * // transliterated names) is a solid offline interim, not the final service.
 */
import worldCountries from 'world-countries'
import type { Country } from 'world-countries'
import citiesDataset from 'countries-cities/data.json'

export interface RefCountry {
  /** Country name, e.g. "United Kingdom". */
  n: string
  /** Demonym (nationality), e.g. "British" — the value that gets stored. */
  dem: string
  /** ISO-3166 alpha-2. */
  iso: string
  /** ISO-3166 alpha-3 (shown mono in typeahead rows). */
  iso3: string
  /** Dial code digits (no +), e.g. "971". */
  d: string
  /** National-number length rule after the country code. */
  min: number
  max: number
  /** City shortlist for the residence-country (curated markets) or the
   *  country's capital(s) (appended countries). */
  c: string[]
}

/** Transcribed from DC `countries()` — launch-priority markets with precise
 *  digit rules + curated city shortlists. Order preserved (UAE first). */
const CURATED_COUNTRIES: RefCountry[] = [
  { n: 'United Arab Emirates', dem: 'Emirati', iso: 'AE', iso3: 'ARE', d: '971', min: 9, max: 9, c: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah'] },
  { n: 'Saudi Arabia', dem: 'Saudi', iso: 'SA', iso3: 'SAU', d: '966', min: 9, max: 9, c: ['Riyadh', 'Jeddah', 'Dammam', 'Mecca', 'Medina', 'Al Khobar'] },
  { n: 'Qatar', dem: 'Qatari', iso: 'QA', iso3: 'QAT', d: '974', min: 8, max: 8, c: ['Doha', 'Al Rayyan', 'Al Wakrah', 'Al Khor', 'Umm Salal'] },
  { n: 'Oman', dem: 'Omani', iso: 'OM', iso3: 'OMN', d: '968', min: 8, max: 8, c: ['Muscat', 'Salalah', 'Sohar', 'Nizwa', 'Sur'] },
  { n: 'Bahrain', dem: 'Bahraini', iso: 'BH', iso3: 'BHR', d: '973', min: 8, max: 8, c: ['Manama', 'Riffa', 'Muharraq', 'Isa Town'] },
  { n: 'Kuwait', dem: 'Kuwaiti', iso: 'KW', iso3: 'KWT', d: '965', min: 8, max: 8, c: ['Kuwait City', 'Hawalli', 'Salmiya', 'Al Jahra'] },
  { n: 'United Kingdom', dem: 'British', iso: 'GB', iso3: 'GBR', d: '44', min: 10, max: 10, c: ['London', 'Manchester', 'Birmingham', 'Edinburgh', 'Glasgow', 'Bristol'] },
  { n: 'United States', dem: 'American', iso: 'US', iso3: 'USA', d: '1', min: 10, max: 10, c: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Boston', 'Denver'] },
  { n: 'France', dem: 'French', iso: 'FR', iso3: 'FRA', d: '33', min: 9, max: 9, c: ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Bordeaux'] },
  { n: 'Germany', dem: 'German', iso: 'DE', iso3: 'DEU', d: '49', min: 10, max: 11, c: ['Berlin', 'Munich', 'Hamburg', 'Cologne', 'Frankfurt', 'Stuttgart'] },
  { n: 'Italy', dem: 'Italian', iso: 'IT', iso3: 'ITA', d: '39', min: 9, max: 10, c: ['Rome', 'Milan', 'Naples', 'Turin', 'Florence', 'Bologna'] },
  { n: 'Spain', dem: 'Spanish', iso: 'ES', iso3: 'ESP', d: '34', min: 9, max: 9, c: ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Bilbao', 'Malaga'] },
  { n: 'Netherlands', dem: 'Dutch', iso: 'NL', iso3: 'NLD', d: '31', min: 9, max: 9, c: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven'] },
  { n: 'India', dem: 'Indian', iso: 'IN', iso3: 'IND', d: '91', min: 10, max: 10, c: ['Mumbai', 'Delhi', 'Bengaluru', 'Chennai', 'Hyderabad', 'Pune'] },
  { n: 'Pakistan', dem: 'Pakistani', iso: 'PK', iso3: 'PAK', d: '92', min: 10, max: 10, c: ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad'] },
  { n: 'Egypt', dem: 'Egyptian', iso: 'EG', iso3: 'EGY', d: '20', min: 10, max: 10, c: ['Cairo', 'Alexandria', 'Giza', 'Luxor', 'Aswan'] },
  { n: 'Jordan', dem: 'Jordanian', iso: 'JO', iso3: 'JOR', d: '962', min: 9, max: 9, c: ['Amman', 'Zarqa', 'Irbid', 'Aqaba'] },
  { n: 'Lebanon', dem: 'Lebanese', iso: 'LB', iso3: 'LBN', d: '961', min: 7, max: 8, c: ['Beirut', 'Tripoli', 'Sidon', 'Byblos'] },
  { n: 'Australia', dem: 'Australian', iso: 'AU', iso3: 'AUS', d: '61', min: 9, max: 9, c: ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'] },
  { n: 'Canada', dem: 'Canadian', iso: 'CA', iso3: 'CAN', d: '1', min: 10, max: 10, c: ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'] },
  { n: 'South Africa', dem: 'South African', iso: 'ZA', iso3: 'ZAF', d: '27', min: 9, max: 9, c: ['Cape Town', 'Johannesburg', 'Durban', 'Pretoria'] },
  { n: 'Kenya', dem: 'Kenyan', iso: 'KE', iso3: 'KEN', d: '254', min: 9, max: 9, c: ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'] },
  { n: 'Japan', dem: 'Japanese', iso: 'JP', iso3: 'JPN', d: '81', min: 10, max: 10, c: ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama', 'Nagoya'] },
  { n: 'China', dem: 'Chinese', iso: 'CN', iso3: 'CHN', d: '86', min: 11, max: 11, c: ['Beijing', 'Shanghai', 'Guangzhou', 'Shenzhen', 'Chengdu'] },
]

/** Map one `world-countries` record into a `RefCountry`. Dial code = idd.root +
 *  the first idd suffix (per §11.8 seam note), stripped of '+'. Demonym falls
 *  back to the country name when the dataset has none; cities are the capital
 *  list (permissive E.164 digit bounds, since precise lengths are unknown). */
function fromWorldCountry(c: Country): RefCountry {
  const root = c.idd?.root ?? ''
  const suffix = c.idd?.suffixes?.[0] ?? ''
  return {
    n: c.name.common,
    dem: c.demonyms?.eng?.m || c.name.common,
    iso: c.cca2,
    iso3: c.cca3,
    d: `${root}${suffix}`.replace(/\+/g, ''),
    min: 4,
    max: 15,
    c: c.capital ?? [],
  }
}

/** Full ISO-3166 country list: curated launch markets first (precise data),
 *  then every other country from the maintained dataset. Built once at load. */
export const REF_COUNTRIES: RefCountry[] = (() => {
  const curatedIsos = new Set(CURATED_COUNTRIES.map((c) => c.iso))
  const rest = (worldCountries as Country[])
    .filter((c) => !curatedIsos.has(c.cca2))
    .map(fromWorldCountry)
  return [...CURATED_COUNTRIES, ...rest]
})()

export const countryByIso = (iso: string): RefCountry | null =>
  REF_COUNTRIES.find((c) => c.iso === iso) ?? null

export const countryByDial = (dial: string): RefCountry | null =>
  REF_COUNTRIES.find((c) => c.d === dial) ?? null

/** Dial-code select options `{ISO} +{dial}`, sorted by country name (DC). */
export const dialOptions = (): string[] =>
  REF_COUNTRIES.slice()
    .sort((a, b) => a.n.localeCompare(b.n))
    .map((c) => `${c.iso} +${c.d}`)

/** Nationality typeahead: matches demonym/country/ISO2/ISO3, demonym-prefix
 *  matches first, max 6 (canon §5.3; algorithm transcribed from DC). */
export function nationalityMatches(query: string): RefCountry[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const hit = REF_COUNTRIES.filter(
    (c) =>
      c.dem.toLowerCase().includes(q) ||
      c.n.toLowerCase().includes(q) ||
      c.iso.toLowerCase() === q ||
      c.iso3.toLowerCase() === q,
  )
  hit.sort(
    (a, b) =>
      (b.dem.toLowerCase().startsWith(q) ? 1 : 0) - (a.dem.toLowerCase().startsWith(q) ? 1 : 0),
  )
  return hit.slice(0, 6)
}

/** Residence typeahead: country name / ISO2 / ISO3, max 6 (DC). */
export function residenceMatches(query: string): RefCountry[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return REF_COUNTRIES.filter(
    (c) => c.n.toLowerCase().includes(q) || c.iso.toLowerCase() === q || c.iso3.toLowerCase() === q,
  ).slice(0, 6)
}

// ---------------------------------------------------------------------------
// City dataset (`countries-cities`) — merged with the curated seed (CMT-100427)
// ---------------------------------------------------------------------------

/** The dataset is keyed by English country NAME (no ISO). Most `RefCountry.n`
 *  match a key directly; a normalised index catches punctuation/spacing drift,
 *  and this table covers the few where the maintained ISO name differs from the
 *  dataset's 2015 name. Anything still unmatched falls back to the capital seed. */
const CITY_KEY_OVERRIDES: Record<string, string> = {
  Türkiye: 'Turkey',
  Taiwan: 'Taiwan, Province of China',
}

const CITY_DB: Record<string, string[]> = citiesDataset.countries

const normCountry = (s: string): string => s.toLowerCase().replace(/[^a-z]/g, '')

/** name (normalised) → dataset key, built once at load. */
const cityKeyByNorm: Record<string, string> = (() => {
  const m: Record<string, string> = {}
  for (const k of Object.keys(CITY_DB)) m[normCountry(k)] = k
  return m
})()

/** Dataset city list for a country (major cities), or [] when unmatched. */
function datasetCities(country: RefCountry): string[] {
  const key =
    CITY_KEY_OVERRIDES[country.n] ??
    (CITY_DB[country.n] ? country.n : cityKeyByNorm[normCountry(country.n)])
  return key ? CITY_DB[key] ?? [] : []
}

/** Full city list offered for a country: curated seed FIRST (launch-market
 *  ordering preserved), then the dataset's major cities, deduped case-insens. */
export function citiesFor(country: RefCountry): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const c of [...country.c, ...datasetCities(country)]) {
    const k = c.trim().toLowerCase()
    if (k && !seen.has(k)) {
      seen.add(k)
      out.push(c)
    }
  }
  return out
}

/** City typeahead within the picked residence country: searches the merged
 *  curated+dataset list, prefix matches first, max 8 (CMT-100427 widened it
 *  from the old capital-only allow-list). Empty query previews the top cities. */
export function cityMatches(country: RefCountry, query: string): string[] {
  const q = query.trim().toLowerCase()
  const all = citiesFor(country)
  if (!q) return all.slice(0, 8)
  const hits = all.filter((c) => c.toLowerCase().includes(q))
  hits.sort(
    (a, b) => (b.toLowerCase().startsWith(q) ? 1 : 0) - (a.toLowerCase().startsWith(q) ? 1 : 0),
  )
  return hits.slice(0, 8)
}

/** True when `city` is a known city of the country (curated seed or dataset),
 *  case-insensitive. Drives the wizard's "use my entry" free-text affordance:
 *  when this is false, the user can still deliberately pick their typed value. */
export function cityKnown(country: RefCountry, city: string): boolean {
  const t = city.trim().toLowerCase()
  if (!t) return false
  return citiesFor(country).some((c) => c.toLowerCase() === t)
}
